import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CS } from "@/constants/testIds/clickshield";

const PLATFORMS = {
  google: {
    name: "Google Ads",
    brand: "Google",
    color: "#1a73e8",
    logo: (
      <span className="text-2xl font-bold">
        <span style={{ color: "#4285F4" }}>G</span>
        <span style={{ color: "#EA4335" }}>o</span>
        <span style={{ color: "#FBBC05" }}>o</span>
        <span style={{ color: "#4285F4" }}>g</span>
        <span style={{ color: "#34A853" }}>l</span>
        <span style={{ color: "#EA4335" }}>e</span>
      </span>
    ),
    scopes: ["View your Google Ads campaigns", "Manage IP exclusion lists", "View click and cost reports"],
  },
  meta: {
    name: "Meta Business",
    brand: "Meta",
    color: "#0866FF",
    logo: <span className="text-2xl font-bold" style={{ color: "#0866FF" }}>Meta</span>,
    scopes: ["Access your ad accounts", "Manage audience exclusions", "Read ads insights and reports"],
  },
  bing: {
    name: "Microsoft Advertising",
    brand: "Microsoft",
    color: "#00A4EF",
    logo: <span className="text-2xl font-bold" style={{ color: "#00A4EF" }}>Microsoft</span>,
    scopes: ["View your advertising campaigns", "Manage IP exclusions", "Read performance reports"],
  },
};

export default function OAuthConsent() {
  const { platform } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const cfg = PLATFORMS[platform];

  const closeWith = (status) => {
    if (window.opener) {
      window.opener.postMessage({ type: "clickshield-oauth", status, platform }, window.location.origin);
      window.close();
    } else {
      nav("/dashboard");
    }
  };

  const allow = async () => {
    setBusy(true);
    try {
      await api.post(`/oauth/${platform}/grant`);
      closeWith("success");
    } catch {
      setBusy(false);
      closeWith("error");
    }
  };

  if (!cfg)
    return (
      <div className="min-h-screen bg-[#f1f3f4] flex items-center justify-center text-slate-600 text-sm">
        Unknown platform.
      </div>
    );

  return (
    <div className="min-h-screen bg-[#f1f3f4] flex items-center justify-center p-4 font-sans">
      <div data-testid={CS.oauthConsentCard} className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        <div className="flex items-center justify-center mb-6">{cfg.logo}</div>
        <div className="flex items-center justify-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#040914] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
          </div>
          <span className="text-slate-400 text-lg">⇄</span>
          <div className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center text-sm font-bold" style={{ color: cfg.color }}>
            {cfg.brand[0]}
          </div>
        </div>
        <h1 className="text-xl font-semibold text-slate-900 text-center">
          ClickShield AI wants to access your {cfg.name} account
        </h1>
        <p className="text-sm text-slate-500 text-center mt-2">{user?.email}</p>

        <div className="mt-6 rounded-xl border border-slate-200 divide-y divide-slate-100">
          {cfg.scopes.map((s) => (
            <div key={s} className="flex items-start gap-3 px-4 py-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
              <span className="text-sm text-slate-700">{s}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-400 mt-4 leading-relaxed">
          By clicking Allow, you permit ClickShield AI to monitor this ad account for fraudulent clicks and automatically apply IP exclusions. You can revoke access at any time.
        </p>

        <div className="mt-6 flex gap-3">
          <button
            data-testid={CS.oauthConsentCancel}
            onClick={() => closeWith("denied")}
            disabled={busy}
            className="flex-1 rounded-full border border-slate-300 text-slate-700 text-sm font-medium py-2.5 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid={CS.oauthConsentAllow}
            onClick={allow}
            disabled={busy}
            className="flex-1 rounded-full text-white text-sm font-medium py-2.5 hover:brightness-110 transition-[filter] flex items-center justify-center gap-2 disabled:opacity-70"
            style={{ background: cfg.color }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Allow
          </button>
        </div>
      </div>
    </div>
  );
}
